(function () {
  'use strict';

  class Shopify3DStickerCustomizer {
    constructor(root) {
      this.root = root;
      this.container = root.querySelector('.tumbler-3d-container');

      if (!this.container) return;

      this.sectionId = this.container.dataset.sectionId;

      this.configInput = document.getElementById('sticker-config-' + this.sectionId);
      this.previewInput = document.getElementById('sticker-preview-' + this.sectionId);
      this.scaleInput = document.getElementById('sticker-scale-' + this.sectionId);
      this.rotationInput = document.getElementById('sticker-rotation-' + this.sectionId);

      this.removeBtn = document.getElementById('remove-selected-sticker-' + this.sectionId);
      this.clearBtn = document.getElementById('clear-stickers-' + this.sectionId);
      this.placeBtn = document.getElementById('place-sticker-' + this.sectionId);

      this.moveBtn = document.getElementById('move-sticker-' + this.sectionId);
      this.scaleBtn = document.getElementById('scale-sticker-' + this.sectionId);
      this.rotateBtn = document.getElementById('rotate-sticker-' + this.sectionId);

      this.searchInput = document.getElementById('sticker-search-' + this.sectionId);
      this.loadingEl = this.container.querySelector('.tumbler-3d-container__loading');

      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.controls = null;
      this.transformControls = null;
      this.raycaster = null;
      this.pointer = null;

      this.modelRoot = null;
      this.targetMesh = null;
      this.stickerLayer = null;
      this.targetMeshName = this.container.dataset.targetMeshName || 'TumblerBody';

      this.selectedStickerTexture = null;
      this.selectedStickerUrl = null;
      this.selectedStickerButton = null;

      this.decals = [];
      this.selectedDecal = null;
      this.placementMode = false;
      this.lastSaveAt = 0;

      this.init().catch((error) => {
        console.error('[3D Customizer] Error inicializando:', error);
        if (this.loadingEl) {
          this.loadingEl.textContent = 'No se pudo cargar el personalizador 3D.';
        }
      });
    }

    async init() {
      await this.loadThree();
      this.setupScene();
      this.bindUI();

      const modelUrl = this.container.dataset.modelUrl;
      if (!modelUrl) throw new Error('No se encontró data-model-url.');

      await this.loadModel(modelUrl);

      this.hideLoading();
      this.selectFirstSticker();
      this.animate();
      this.saveState(true);
    }

    async loadThree() {
      if (
        window.THREE &&
        window.THREE.GLTFLoader &&
        window.THREE.OrbitControls &&
        window.THREE.TransformControls
      ) {
        return;
      }

      const threeModule = await import('three');

      const extras = {};
      window.THREE = new Proxy(threeModule, {
        get(target, prop) {
          if (prop in extras) return extras[prop];
          return target[prop];
        },
        set(target, prop, value) {
          extras[prop] = value;
          return true;
        },
        has(target, prop) {
          return prop in extras || prop in target;
        }
      });

      const gltfModule = await import('three/addons/loaders/GLTFLoader.js');
      window.THREE.GLTFLoader = gltfModule.GLTFLoader;

      const controlsModule = await import('three/addons/controls/OrbitControls.js');
      window.THREE.OrbitControls = controlsModule.OrbitControls;

      const transformModule = await import('three/addons/controls/TransformControls.js');
      window.THREE.TransformControls = transformModule.TransformControls;
    }

    setupScene() {
      const THREE = window.THREE;
      const width = this.container.clientWidth || 600;
      const height = this.container.clientHeight || 560;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf2f2f2);

      this.camera = new THREE.PerspectiveCamera(30, width / height, 0.01, 100);
      this.camera.position.set(0, 0, 4);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true
      });

      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.setSize(width, height);
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.NoToneMapping;
      this.container.appendChild(this.renderer.domElement);

      this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
      this.controls.target.set(0, 0, 0);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.08;
      this.controls.minDistance = 2;
      this.controls.maxDistance = 8;
      this.controls.enablePan = false;
      this.controls.update();

      this.transformControls = new THREE.TransformControls(this.camera, this.renderer.domElement);
      this.transformControls.setMode('translate');
      this.transformControls.setSize(0.75);
      this.transformControls.visible = false;

      this.transformControls.addEventListener('dragging-changed', (event) => {
        this.controls.enabled = !event.value;
      });

      this.transformControls.addEventListener('objectChange', () => {
        this.syncSelectedStickerFromTransform();
        this.saveState();
      });

      this.scene.add(this.transformControls);

      this.raycaster = new THREE.Raycaster();
      this.pointer = new THREE.Vector2();

      this.scene.add(new THREE.AmbientLight(0xffffff, 1.25));

      const hemiLight = new THREE.HemisphereLight(0xffffff, 0xd8d8d8, 1.2);
      this.scene.add(hemiLight);

      const keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
      keyLight.position.set(4, 5, 6);
      this.scene.add(keyLight);

      const fillLight = new THREE.DirectionalLight(0xffffff, 0.65);
      fillLight.position.set(-4, 3, 4);
      this.scene.add(fillLight);

      const rimLight = new THREE.DirectionalLight(0xffffff, 0.45);
      rimLight.position.set(0, 2, -5);
      this.scene.add(rimLight);

      window.addEventListener('resize', () => this.onResize(), { passive: true });

      this.renderer.domElement.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    }

    async loadModel(url) {
      const THREE = window.THREE;
      const loader = new THREE.GLTFLoader();

      return new Promise((resolve, reject) => {
        loader.load(
          url,
          (gltf) => {
            this.modelRoot = gltf.scene;

            let namedMesh = null;
            let firstMesh = null;
            const meshNames = [];

            this.modelRoot.traverse((child) => {
              if (!child.isMesh) return;

              meshNames.push(child.name || '(sin nombre)');

              if (!firstMesh) firstMesh = child;
              if (this.targetMeshName && child.name === this.targetMeshName) namedMesh = child;
            });

            console.log('[3D Customizer] Mallas encontradas:', meshNames);

            this.targetMesh = namedMesh || firstMesh;

            if (!this.targetMesh) {
              reject(new Error('El GLB no contiene ninguna malla usable.'));
              return;
            }

            this.centerAndScaleModel(THREE);

            this.modelRoot.traverse((child) => {
              if (!child.isMesh) return;

              child.material = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                roughness: 0.72,
                metalness: 0.0,
                side: THREE.DoubleSide
              });

              child.castShadow = false;
              child.receiveShadow = false;

              if (child.geometry) child.geometry.computeVertexNormals();
            });

            this.scene.add(this.modelRoot);
            this.modelRoot.updateMatrixWorld(true);

            this.createStickerLayer();

            resolve();
          },
          undefined,
          () => reject(new Error('[3D Customizer] Error cargando modelo: ' + url))
        );
      });
    }

    centerAndScaleModel(THREE) {
      const box = new THREE.Box3().setFromObject(this.modelRoot);
      const size = box.getSize(new THREE.Vector3());
      const maxAxis = Math.max(size.x, size.y, size.z);

      if (maxAxis > 0) {
        const desiredSize = 1.65;
        this.modelRoot.scale.setScalar(desiredSize / maxAxis);
      }

      this.modelRoot.updateMatrixWorld(true);

      const boxAfterScale = new THREE.Box3().setFromObject(this.modelRoot);
      const center = boxAfterScale.getCenter(new THREE.Vector3());

      this.modelRoot.position.sub(center);
      this.modelRoot.updateMatrixWorld(true);
    }

    createStickerLayer() {
      const THREE = window.THREE;

      const box = new THREE.Box3().setFromObject(this.targetMesh);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const radius = Math.max(size.x, size.z) * 0.52;
      const height = size.y;

      const geometry = new THREE.CylinderGeometry(
        radius + 0.012,
        radius + 0.012,
        height,
        128,
        1,
        true
      );

      const material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false
      });

      this.stickerLayer = new THREE.Mesh(geometry, material);
      this.stickerLayer.name = 'StickerLayer';
      this.stickerLayer.position.copy(center);

      this.scene.add(this.stickerLayer);
    }

    bindUI() {
      const THREE = window.THREE;
      const stickerButtons = this.root.querySelectorAll('.sticker-grid__item');

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          const query = this.searchInput.value.toLowerCase().trim();

          stickerButtons.forEach((btn) => {
            const text = (btn.dataset.search || '').toLowerCase();
            btn.style.display = !query || text.includes(query) ? '' : 'none';
          });
        });
      }

      stickerButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.selectedStickerButton) {
            this.selectedStickerButton.classList.remove('is-active');
          }

          btn.classList.add('is-active');

          this.selectedStickerButton = btn;
          this.selectedStickerUrl = btn.dataset.stickerUrl;

          const loader = new THREE.TextureLoader();
          loader.setCrossOrigin('anonymous');

          this.selectedStickerTexture = loader.load(
            this.selectedStickerUrl,
            (texture) => {
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.needsUpdate = true;
            }
          );

          this.enablePlacementMode();
        });
      });

      if (this.placeBtn) {
        this.placeBtn.addEventListener('click', () => {
          if (!this.selectedStickerTexture || !this.selectedStickerUrl) return;
          this.enablePlacementMode();
        });
      }

      if (this.moveBtn) {
        this.moveBtn.addEventListener('click', () => this.setTransformMode('translate'));
      }

      if (this.scaleBtn) {
        this.scaleBtn.addEventListener('click', () => this.setTransformMode('scale'));
      }

      if (this.rotateBtn) {
        this.rotateBtn.addEventListener('click', () => this.setTransformMode('rotate'));
      }

      if (this.scaleInput) {
        this.scaleInput.addEventListener('input', () => {
          if (!this.selectedDecal) return;

          const value = Number.parseFloat(this.scaleInput.value);
          this.selectedDecal.scale.setScalar(value / this.selectedDecal.userData.scaleValue);
          this.selectedDecal.userData.scaleValue = value;
          this.saveState(true);
        });
      }

      if (this.rotationInput) {
        this.rotationInput.addEventListener('input', () => {
          if (!this.selectedDecal) return;

          const value = Number.parseFloat(this.rotationInput.value);
          this.selectedDecal.userData.rotationValue = value;
          this.rebuildSelectedDecal();
        });
      }

      if (this.removeBtn) this.removeBtn.addEventListener('click', () => this.removeSelectedDecal());
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearDecals());

      const form = document.getElementById('product-form-' + this.sectionId);
      if (form) form.addEventListener('submit', () => this.saveState(true));
    }

    enablePlacementMode() {
      this.placementMode = true;
      this.root.classList.add('is-placing-sticker');
      this.detachTransform();
      if (this.renderer) this.renderer.domElement.style.cursor = 'crosshair';
    }

    disablePlacementMode() {
      this.placementMode = false;
      this.root.classList.remove('is-placing-sticker');
      if (this.renderer) this.renderer.domElement.style.cursor = '';
    }

    setTransformMode(mode) {
      if (!this.selectedDecal || !this.transformControls) return;
      this.transformControls.setMode(mode);
      this.transformControls.attach(this.selectedDecal);
      this.transformControls.visible = true;
    }

    detachTransform() {
      if (!this.transformControls) return;
      this.transformControls.detach();
      this.transformControls.visible = false;
    }

    selectFirstSticker() {
      const first = this.root.querySelector('.sticker-grid__item');
      if (first) first.click();
    }

    hideLoading() {
      if (this.loadingEl) this.loadingEl.style.display = 'none';
    }

    getPointer(event) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    getIntersections(event, object) {
      this.getPointer(event);
      this.raycaster.setFromCamera(this.pointer, this.camera);
      return this.raycaster.intersectObject(object, true);
    }

    onPointerDown(event) {
      if (!this.targetMesh || !this.stickerLayer) return;

      const decalHit = this.raycasterStickerHit(event);

      if (decalHit) {
        event.preventDefault();
        this.selectDecal(decalHit.object);
        this.disablePlacementMode();
        return;
      }

      if (!this.placementMode) return;
      if (!this.selectedStickerTexture || !this.selectedStickerUrl) return;

      event.preventDefault();

      const hits = this.getIntersections(event, this.stickerLayer);
      if (!hits.length) return;

      this.createStickerFromHit(hits[0]);
      this.disablePlacementMode();
    }

    raycasterStickerHit(event) {
      if (!this.decals.length) return null;

      this.getPointer(event);
      this.raycaster.setFromCamera(this.pointer, this.camera);

      const hits = this.raycaster.intersectObjects(this.decals, true);
      return hits.length ? hits[0] : null;
    }

    createStickerFromHit(hit) {
      let normal = hit.point
        .clone()
        .sub(this.stickerLayer.position)
        .normalize();

      normal.y = 0;
      normal.normalize();

      const position = hit.point.clone();
      const rotationValue = Number.parseFloat(this.rotationInput ? this.rotationInput.value : '0') || 0;
      const scaleValue = Number.parseFloat(this.scaleInput ? this.scaleInput.value : '0.22') || 0.22;

      const sticker = this.buildStickerMesh({
        texture: this.selectedStickerTexture,
        textureUrl: this.selectedStickerUrl,
        position,
        normal,
        rotationValue,
        scaleValue
      });

      this.scene.add(sticker);
      this.decals.push(sticker);
      this.selectDecal(sticker);
      this.saveState(true);
    }

    buildStickerMesh(data) {
      const THREE = window.THREE;

      const geometry = new THREE.PlaneGeometry(
        data.scaleValue,
        data.scaleValue
      );

      const material = new THREE.MeshBasicMaterial({
        map: data.texture,
        transparent: true,
        alphaTest: 0.001,
        side: THREE.FrontSide,
        depthTest: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -4
      });

      const sticker = new THREE.Mesh(geometry, material);
      sticker.renderOrder = 10;

      const normal = data.normal.clone().normalize();
      const offset = data.position.clone().add(normal.clone().multiplyScalar(0.006));

      sticker.position.copy(offset);

      const lookTarget = offset.clone().add(normal);
      sticker.lookAt(lookTarget);
      sticker.rotateZ(data.rotationValue);

      sticker.userData = {
        textureUrl: data.textureUrl,
        position: data.position,
        normal: data.normal,
        rotationValue: data.rotationValue,
        scaleValue: data.scaleValue,
        projectionMode: 'sticker-layer'
      };

      return sticker;
    }

    rebuildSelectedDecal() {
      if (!this.selectedDecal) return;

      const old = this.selectedDecal;
      const data = old.userData;

      const texture = new window.THREE.TextureLoader().load(
        data.textureUrl,
        (loadedTexture) => {
          loadedTexture.colorSpace = window.THREE.SRGBColorSpace;
          loadedTexture.needsUpdate = true;
        }
      );

      const replacement = this.buildStickerMesh({
        texture,
        textureUrl: data.textureUrl,
        position: data.position,
        normal: data.normal,
        rotationValue: data.rotationValue,
        scaleValue: data.scaleValue
      });

      this.scene.remove(old);

      if (old.geometry) old.geometry.dispose();
      if (old.material) {
        if (old.material.map) old.material.map.dispose();
        old.material.dispose();
      }

      const idx = this.decals.indexOf(old);
      if (idx >= 0) this.decals[idx] = replacement;

      this.scene.add(replacement);
      this.selectedDecal = replacement;
      this.transformControls.attach(replacement);
      this.saveState(true);
    }

    syncSelectedStickerFromTransform() {
      if (!this.selectedDecal) return;

      const THREE = window.THREE;
      const sticker = this.selectedDecal;

      sticker.userData.position = sticker.position.clone();

      const normal = sticker.position
        .clone()
        .sub(this.stickerLayer.position)
        .normalize();

      normal.y = 0;
      normal.normalize();

      sticker.userData.normal = normal;
      sticker.userData.scaleValue = sticker.scale.x * (sticker.geometry.parameters.width || sticker.userData.scaleValue);

      const euler = sticker.rotation;
      sticker.userData.rotationValue = euler.z;

      if (this.scaleInput) this.scaleInput.value = sticker.userData.scaleValue;
      if (this.rotationInput) this.rotationInput.value = sticker.userData.rotationValue;
    }

    updateStickerVisibility() {
      if (!this.camera || !this.decals.length) return;

      const THREE = window.THREE;
      const cameraDir = new THREE.Vector3();
      this.camera.getWorldDirection(cameraDir);

      const viewDir = cameraDir.clone().negate().normalize();

      this.decals.forEach((sticker) => {
        const normal = sticker.userData.normal.clone().normalize();
        const visibility = normal.dot(viewDir);

        if (visibility <= 0.05) {
          sticker.visible = false;
          return;
        }

        sticker.visible = true;

        if (sticker.material) {
          sticker.material.opacity = Math.min(1, Math.max(0.25, visibility));
          sticker.material.needsUpdate = true;
        }
      });
    }

    selectDecal(decal) {
      this.selectedDecal = decal;

      if (this.scaleInput) this.scaleInput.value = decal.userData.scaleValue;
      if (this.rotationInput) this.rotationInput.value = decal.userData.rotationValue;

      this.transformControls.attach(decal);
      this.transformControls.visible = true;
      this.transformControls.setMode('translate');
    }

    removeSelectedDecal() {
      if (!this.selectedDecal) return;

      const target = this.selectedDecal;

      this.detachTransform();

      this.scene.remove(target);
      this.decals = this.decals.filter((d) => d !== target);

      if (target.geometry) target.geometry.dispose();
      if (target.material) {
        if (target.material.map) target.material.map.dispose();
        target.material.dispose();
      }

      this.selectedDecal = null;
      this.saveState(true);
    }

    clearDecals() {
      this.detachTransform();

      this.decals.forEach((decal) => {
        this.scene.remove(decal);

        if (decal.geometry) decal.geometry.dispose();
        if (decal.material) {
          if (decal.material.map) decal.material.map.dispose();
          decal.material.dispose();
        }
      });

      this.decals = [];
      this.selectedDecal = null;
      this.saveState(true);
    }

    serializeDecals() {
      return this.decals.map((decal, index) => ({
        index,
        textureUrl: decal.userData.textureUrl,
        position: decal.userData.position.toArray(),
        normal: decal.userData.normal.toArray(),
        rotationValue: decal.userData.rotationValue,
        scaleValue: decal.userData.scaleValue,
        projectionMode: decal.userData.projectionMode || 'sticker-layer'
      }));
    }

    saveState(forcePreview) {
      const now = Date.now();
      if (!forcePreview && now - this.lastSaveAt < 120) return;

      this.lastSaveAt = now;

      if (this.configInput) {
        this.configInput.value = JSON.stringify({
          version: '1.0.5-transform-controls',
          modelUrl: this.container.dataset.modelUrl,
          targetMeshName: this.targetMeshName,
          decals: this.serializeDecals()
        });
      }

      if (this.previewInput && (forcePreview || this.decals.length)) {
        try {
          this.previewInput.value = this.renderer.domElement.toDataURL('image/png');
        } catch (error) {
          console.warn('[3D Customizer] No se pudo generar preview PNG:', error);
          this.previewInput.value = '';
        }
      }
    }

    onResize() {
      if (!this.renderer || !this.camera) return;

      const width = this.container.clientWidth;
      const height = this.container.clientHeight;

      if (!width || !height) return;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }

    animate() {
      requestAnimationFrame(() => this.animate());

      if (this.controls) this.controls.update();
      this.updateStickerVisibility();

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.product-3d-customizer').forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      new Shopify3DStickerCustomizer(root);
    });
  });
})();